import React, { useState, useEffect } from 'react';
import { useApp } from '../../../context/AppContext';
import apiService from '../../../services/apiService';
import Modal from '../../../components/ui/Modal';

const DatosFiscalesForm = ({ cliente, catalogos, onClose, onSaved }) => {
  const { showToast, setLoading } = useApp();

  const [formData, setFormData] = useState({
    cliente_id: cliente?.id || 0,
    rfc: '',
    razon_social: '',
    regimen_fiscal: '',
    uso_cfdi: '',
    codigo_postal_fiscal: '',
    email_facturacion: '',
    requiere_factura: false
  });

  const [errors, setErrors] = useState({});

  // Cargar datos existentes
  useEffect(() => {
    if (cliente) {
      loadDatosFiscales();
    }
  }, [cliente]);

  const loadDatosFiscales = async () => {
    try {
      setLoading(true);
      const result = await apiService.getDatosFiscalesCliente(cliente.id);
      if (result.success && result.data) {
        setFormData({
          cliente_id: cliente.id,
          rfc: result.data.rfc || '',
          razon_social: result.data.razon_social || '',
          regimen_fiscal: result.data.regimen_fiscal || '',
          uso_cfdi: result.data.uso_cfdi || '',
          codigo_postal_fiscal: result.data.codigo_postal_fiscal || '',
          email_facturacion: result.data.email_facturacion || '',
          requiere_factura: result.data.requiere_factura || false
        });
      }
    } catch (error) {
      console.error('Error cargando datos fiscales:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.rfc.trim()) {
      newErrors.rfc = 'RFC es requerido';
    } else {
      const rfcLength = formData.rfc.trim().length;
      if (rfcLength !== 12 && rfcLength !== 13) {
        newErrors.rfc = 'RFC debe tener 12 (moral) o 13 (física) caracteres';
      }
    }

    if (!formData.razon_social.trim()) {
      newErrors.razon_social = 'Razón social es requerida';
    }

    if (!formData.regimen_fiscal) {
      newErrors.regimen_fiscal = 'Régimen fiscal es requerido';
    }

    if (!formData.codigo_postal_fiscal.trim()) {
      newErrors.codigo_postal_fiscal = 'Código postal es requerido';
    } else if (!/^\d{5}$/.test(formData.codigo_postal_fiscal.trim())) {
      newErrors.codigo_postal_fiscal = 'Código postal debe tener 5 dígitos';
    }

    if (formData.email_facturacion && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email_facturacion)) {
      newErrors.email_facturacion = 'Email inválido';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      const result = await apiService.actualizarDatosFiscales({
        ...formData,
        rfc: formData.rfc.toUpperCase().trim()
      });

      if (result.success) {
        showToast('Datos fiscales actualizados', 'success');
        if (onSaved) {
          onSaved(formData);
        }
        onClose();
      } else {
        showToast(result.message || 'Error al actualizar', 'error');
      }
    } catch (error) {
      showToast('Error al guardar datos fiscales', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Determinar tipo de persona por longitud de RFC
  const getTipoPersona = () => {
    const rfcLength = formData.rfc.trim().length;
    if (rfcLength === 12) return 'moral';
    if (rfcLength === 13) return 'fisica';
    return null;
  };

  // Filtrar regímenes según tipo de persona
  const getRegimenesFiltrados = () => {
    const tipoPersona = getTipoPersona();
    if (!tipoPersona) return catalogos.regimenesFiscales;

    return catalogos.regimenesFiscales.filter(r =>
      tipoPersona === 'fisica' ? r.persona_fisica : r.persona_moral
    );
  };

  // Filtrar usos CFDI según tipo de persona
  const getUsosCFDIFiltrados = () => {
    const tipoPersona = getTipoPersona();
    if (!tipoPersona) return catalogos.usosCFDI;

    return catalogos.usosCFDI.filter(u =>
      tipoPersona === 'fisica' ? u.persona_fisica : u.persona_moral
    );
  };

  return (
    <Modal
      title={`Datos Fiscales - ${cliente?.nombre || 'Cliente'}`}
      onClose={onClose}
      size="medium"
    >
      <form onSubmit={handleSubmit} className="modal-facturacion">
        <div className="form-row">
          <div className="form-group">
            <label>
              RFC <span className="required">*</span>
            </label>
            <input
              type="text"
              value={formData.rfc}
              onChange={(e) => handleChange('rfc', e.target.value.toUpperCase())}
              placeholder="XAXX010101000"
              maxLength={13}
              className={errors.rfc ? 'error' : ''}
            />
            {errors.rfc && <span className="error-message">{errors.rfc}</span>}
          </div>

          <div className="form-group">
            <label>
              Código Postal Fiscal <span className="required">*</span>
            </label>
            <input
              type="text"
              value={formData.codigo_postal_fiscal}
              onChange={(e) => handleChange('codigo_postal_fiscal', e.target.value.replace(/\D/g, ''))}
              placeholder="12345"
              maxLength={5}
              className={errors.codigo_postal_fiscal ? 'error' : ''}
            />
            {errors.codigo_postal_fiscal && <span className="error-message">{errors.codigo_postal_fiscal}</span>}
          </div>
        </div>

        <div className="form-group">
          <label>
            Razón Social <span className="required">*</span>
          </label>
          <input
            type="text"
            value={formData.razon_social}
            onChange={(e) => handleChange('razon_social', e.target.value)}
            placeholder="Nombre o Razón Social"
            className={errors.razon_social ? 'error' : ''}
          />
          {errors.razon_social && <span className="error-message">{errors.razon_social}</span>}
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>
              Régimen Fiscal <span className="required">*</span>
            </label>
            <select
              value={formData.regimen_fiscal}
              onChange={(e) => handleChange('regimen_fiscal', e.target.value)}
              className={errors.regimen_fiscal ? 'error' : ''}
            >
              <option value="">Seleccionar...</option>
              {getRegimenesFiltrados().map(r => (
                <option key={r.clave} value={r.clave}>
                  {r.clave} - {r.descripcion}
                </option>
              ))}
            </select>
            {errors.regimen_fiscal && <span className="error-message">{errors.regimen_fiscal}</span>}
          </div>

          <div className="form-group">
            <label>Uso CFDI (por defecto)</label>
            <select
              value={formData.uso_cfdi}
              onChange={(e) => handleChange('uso_cfdi', e.target.value)}
            >
              <option value="">Seleccionar...</option>
              {getUsosCFDIFiltrados().map(u => (
                <option key={u.clave} value={u.clave}>
                  {u.clave} - {u.descripcion}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-group">
          <label>Email para Facturación</label>
          <input
            type="email"
            value={formData.email_facturacion}
            onChange={(e) => handleChange('email_facturacion', e.target.value)}
            placeholder="facturacion@ejemplo.com"
            className={errors.email_facturacion ? 'error' : ''}
          />
          {errors.email_facturacion && <span className="error-message">{errors.email_facturacion}</span>}
        </div>

        <div className="form-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={formData.requiere_factura}
              onChange={(e) => handleChange('requiere_factura', e.target.checked)}
            />
            <span>Este cliente generalmente requiere factura</span>
          </label>
        </div>

        <div className="modal-actions">
          <button type="button" className="btn-cancelar" onClick={onClose}>
            Cancelar
          </button>
          <button type="submit" className="btn-guardar">
            Guardar Datos Fiscales
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default DatosFiscalesForm;
